/**
 * Verify-page math computation.
 *
 * The recon engine (backend/.../txnMatchEngine.ts) computes per-ISIN
 * status from PF + DP txns using pure arithmetic. To show the
 * reviewer HOW that status was derived, we re-run the same arithmetic
 * here in the frontend over the recon_txn_pair rows that the backend
 * already persists, and produce a structured trace.
 *
 * Why re-compute instead of asking the backend for the trace:
 *   - No DB schema change. recon_row stores the final numbers; the
 *     intermediate steps aren't persisted.
 *   - The engine arithmetic is small (~50 lines). Mirroring it here
 *     is cheaper than designing/migrating a trace table.
 *   - Trace stays in sync with the engine as long as the rules in
 *     this file match the rules in txnMatchEngine.ts. The mapping
 *     is documented inline so any future engine change can be
 *     mirrored here in the same commit.
 *
 * What this is NOT:
 *   - Not the source of truth. The backend engine is. This is a
 *     visual reproduction.
 *   - Not used to make decisions. Reviewer reads it; nothing
 *     downstream consumes the output.
 */

import type { ReconRow, ReconTxnPair } from '@/shared/api';

/* The QTY_TOLERANCE constant must match the backend engine. Bumping
 * it here without bumping it there would produce a misleading trace. */
const QTY_TOLERANCE = 0.001;

/** Per-row contribution to portfolio_net. */
export interface PfTrace {
  pair_id: string;
  date: string | null;
  txn_type: string;
  raw_qty: number;
  /** Effective signed qty after window filtering. Out-of-window rows
   *  always have signed_qty = 0 (regardless of buy/sell kind) because
   *  they don't contribute to the recon's net change for this period.
   *  The `kind` field stays accurate so the modal can still show the
   *  txn type meaningfully. */
  signed_qty: number;
  kind: 'buy_like' | 'sell_like' | 'dividend' | 'unknown';
  /** True when this txn fell outside [statement_from, statement_to].
   *  Engine excluded it from portfolio_net; the math modal greys it
   *  out so the reviewer can see it without confusing the totals. */
  outside_window: boolean;
}

/** Per-row contribution to dp_net. */
export interface DpTrace {
  pair_id: string;
  date: string | null;
  description: string;
  credit: number;
  debit: number;
  signed_qty: number;
  /** Sub-section label parsed from a "(...)" suffix in the description.
   *  Empty string if none — covers the common single-section case. */
  sub_section: string;
  slb_classification: 'first' | 'return' | null;
}

/** SLB FIFO pairing result. */
export interface SlbPairing {
  /** FIRST LEG entries that were not matched to a RETURN LEG, contributing to outstanding. */
  unmatched_firsts: Array<{ pair_id: string; date: string; qty: number }>;
  /** Matched pairs (informational, for display). */
  matched: Array<{
    first_pair_id: string; first_date: string; first_qty: number;
    return_pair_id: string; return_date: string; return_qty: number;
    exact_match: boolean;
  }>;
  /** Total qty still out on loan. Sum of unmatched_firsts qty. */
  outstanding: number;
}

export interface MathTrace {
  isin: string;
  display_name: string;
  pf: { rows: PfTrace[]; net: number };
  dp: { rows: DpTrace[]; by_section: Array<{ section: string; rows: DpTrace[]; subtotal: number }>; net: number };
  slb: SlbPairing;
  status: ReconRow['status'];
  /** One-line explanation of the rule that fired. */
  status_reason: string;
}

/* ─── Mirror of txnMatchEngine.signedQty ─────────────────────────────────
 * Keep these two enum lists in sync.
 *
 * Mapping rationale (from engine):
 *   BUY-like         → +qty
 *   SELL-like        → -qty
 *   Dividend-like    →  0  (cash event, no qty movement)
 *   Anything else    →  0  (flagged as unknown; reviewer sees explicitly)
 */
function classifyPfTxnType(txn_type: string): 'buy_like' | 'sell_like' | 'dividend' | 'unknown' {
  const tt = txn_type.trim().toUpperCase();
  const orig = txn_type.trim();
  if (
    tt === 'BUY' || tt === 'BUYCR' || tt === 'BONUS' ||
    tt === 'SPLIT BUY' || tt === 'DEMERGERIN' || tt === 'DEMERGERBUY' ||
    tt === 'TRANSFERIN' || tt === 'INTRANSFER' ||
    tt === 'MERGER BUY' ||
    // Mixed-case CA labels from PF File Gen (Bonus Buy, Rights Buy, ROC Buy, etc.)
    tt === 'BONUS BUY' || tt === 'RIGHTS BUY' || tt === 'ROC BUY' ||
    orig.endsWith('Buy') || orig.endsWith('BUY')
  ) return 'buy_like';
  if (
    tt === 'SELL' || tt === 'SELLCR' ||
    tt === 'SPLIT SELL' || tt === 'DEMERGERSELL' ||
    tt === 'TRANSFEROUT' || tt === 'OUTTRANSFER' ||
    tt === 'MERGER SELL' || tt === 'TRANSFER OUT'
  ) return 'sell_like';
  if (
    tt === 'INTERIM DIVIDEND' || tt === 'FINAL DIVIDEND' ||
    tt === 'SPECIAL DIVIDEND' || tt === 'DEMERGERAMT' ||
    tt === 'INTERIM 1 DIVIDEND' || tt === 'INTERIM 2 DIVIDEND' ||
    tt === 'INTERIM 3 DIVIDEND' || tt === 'INTERIM 4 DIVIDEND' ||
    tt === 'BONUSAMT'
  ) return 'dividend';
  return 'unknown';
}

export function pfSignedQty(txn_type: string, raw_qty: number): number {
  const kind = classifyPfTxnType(txn_type);
  if (kind === 'buy_like')  return Math.abs(raw_qty);
  if (kind === 'sell_like') return -Math.abs(raw_qty);
  return 0;
}

/* ─── Mirror of txnMatchEngine.classifySlb ────────────────────────────── */
function classifySlb(description: string, credit: number, debit: number): 'first' | 'return' | null {
  const desc = description.toLowerCase();
  if (!desc.includes('slb') || !desc.includes('leg')) return null;
  if (desc.includes('first leg')  && debit  > 0) return 'first';
  if (desc.includes('return leg') && credit > 0) return 'return';
  return null;
}

/**
 * Parse the trailing "(...)" sub-section label from a DP holding's
 * security_name. The merger uses this to partition multi-sub-section
 * ISINs (Beneficiary / Pledge / Unconfirmed Pledge / etc) so they
 * stay distinct through dedup.
 *
 * For per-row trace we don't have security_name on individual pairs;
 * we have to infer from the description. Common patterns in NSDL:
 *   - Pledge-related rows have "Pledge" in the description
 *   - SLB rows have "SLB" in the description
 *   - Everything else falls into the default "main" section
 *
 * This is APPROXIMATE — the source-of-truth sub-section comes from
 * the merger's partitioning, which we don't have access to here.
 * The reviewer can see this with the warning hint on the modal.
 */
function inferSubSection(description: string): string {
  const d = description.toLowerCase();
  if (d.includes('pledge')) return 'pledge';
  if (d.includes('slb'))    return 'slb';
  return 'main';
}

/** Returns true if a DP description is a pledge/margin internal operation
 *  that should not count toward the reconciliation net. */
function isPledgeDescription(description: string): boolean {
  const d = description.toLowerCase();
  return d.includes('to pledge') ||
         d.includes('by pledge') ||
         d.includes('pledge request') ||
         d.includes('pledge closure') ||
         d.includes('margin re-pledge');
}

/** Returns true if a DP description is an eDIS/Block Mechanism entry. */
function isEdisDpLine(description: string): boolean {
  const d = description.toLowerCase();
  return d.includes('edis') || d.includes('block mechanism');
}

/** Extract settlement code from a DP description. */
function extractSettlementCode(description: string): string | null {
  const m = description.match(/(?:NORMAL|TRADE FOR TRADE)\/([0-9]+)/i);
  return m ? m[1].trim() : null;
}

/**
 * Build the full math trace for one ISIN given the recon_txn_pair
 * rows the engine considered.
 *
 * Why this works without the original PortfolioTransaction/DpTxn
 * objects: the pair row has exactly the fields we need
 * (pf_txn_type, pf_qty, dp_description, dp_credit, dp_debit), and
 * each pair row corresponds 1:1 with a row the engine summed.
 */
export function computeMathTrace(row: ReconRow, pairs: ReconTxnPair[]): MathTrace {
  // ─── Portfolio side ─────────────────────────────────────────────────
  const pfPairs = pairs.filter(p => p.pf_txn_date != null);
  const pf_rows: PfTrace[] = pfPairs.map(p => {
    const kind = classifyPfTxnType(p.pf_txn_type ?? '');
    const raw  = Number(p.pf_qty ?? 0);
    // Mirror the engine's window filter: pairs with status
    // 'OUTSIDE_WINDOW' don't contribute to portfolio_net. The kind
    // (buy_like / sell_like / dividend) is still reported so the
    // modal shows what the txn would have been, just greyed.
    const outsideWindow = p.status === 'OUTSIDE_WINDOW';
    const signed_qty = outsideWindow ? 0 : pfSignedQty(p.pf_txn_type ?? '', raw);
    return {
      pair_id: p.id,
      date: p.pf_txn_date,
      txn_type: p.pf_txn_type ?? '',
      raw_qty: raw,
      signed_qty,
      kind,
      outside_window: outsideWindow,
    };
  });
  // ─── Intraday round-trip exclusion ─────────────────────────────────
  // When an intraday SELL (same date, equal qty as a BUY) has no corresponding
  // DP debit — meaning the depository never saw the SELL (net delivery = 0) —
  // exclude BOTH the BUY and the SELL from pf_net, AND exclude the BUY's DP
  // credit from dp_net. The round-trip is neutral: holding unchanged, DP unchanged.
  //
  // Detection: group PF pairs by date. For each (date, qty) pair that has both
  // a BUY and a SELL with equal qty, check whether a DP debit of that qty exists
  // on the same date. If not → round-trip that netted to zero in DP → exclude both.
  const intradayExcludedPfIds = new Set<string>();
  const intradayExcludedDpIds = new Set<string>();
  {
    // Group BUYs and SELLs by date+qty
    const buysByDateQty = new Map<string, ReconTxnPair[]>();
    const sellsByDateQty = new Map<string, ReconTxnPair[]>();
    for (const p of pfPairs) {
      if (p.status === 'OUTSIDE_WINDOW') continue;
      const kind = classifyPfTxnType(p.pf_txn_type ?? '');
      const qty = Math.abs(Number(p.pf_qty ?? 0));
      const key = `${p.pf_txn_date}|${qty}`;
      if (kind === 'buy_like') {
        if (!buysByDateQty.has(key)) buysByDateQty.set(key, []);
        buysByDateQty.get(key)!.push(p);
      } else if (kind === 'sell_like') {
        if (!sellsByDateQty.has(key)) sellsByDateQty.set(key, []);
        sellsByDateQty.get(key)!.push(p);
      }
    }
    // For each BUY that has a matching SELL same date+qty, check DP debit
    const dpPairsFlat = pairs.filter(p => p.dp_txn_date != null || p.dp_description != null);
    for (const [key, buyRows] of buysByDateQty.entries()) {
      const sellRows = sellsByDateQty.get(key);
      if (!sellRows || sellRows.length === 0) continue;
      const [date, qtyStr] = key.split('|');
      const qty = Number(qtyStr);
      // Check if a DP debit of this qty exists on the same date (±1 day)
      const hasDebit = dpPairsFlat.some(p => {
        const dr = Number(p.dp_debit ?? 0);
        if (Math.abs(dr - qty) >= 0.001) return false;
        const d = p.dp_txn_date ?? '';
        return d === date || d === new Date(new Date(date).getTime() + 86400000).toISOString().slice(0, 10);
      });
      if (!hasDebit) {
        // This is an intraday round-trip with no DP debit — exclude from net
        buyRows.forEach(r => intradayExcludedPfIds.add(r.id));
        sellRows.forEach(r => intradayExcludedPfIds.add(r.id));
        // Also find the DP credit(s) of this qty that belong to the BUY
        dpPairsFlat.forEach(p => {
          const cr = Number(p.dp_credit ?? 0);
          if (Math.abs(cr - qty) < 0.001) intradayExcludedDpIds.add(p.id);
        });
      }
    }
  }

  const pf_net = pf_rows
    .filter(r => !intradayExcludedPfIds.has(r.pair_id))
    .reduce((s, r) => s + r.signed_qty, 0);

  // ─── DP side ────────────────────────────────────────────────────────
  // Only count reconciliation-relevant DP rows — same filter as TxnReconView:
  //   1. Exclude pledge/margin internal operations
  //   2. Exclude eDIS/Block Mechanism entries with no settlement code that
  //      form a credit+debit noise pair within 8 days (same suppression logic)
  const allDpPairs = pairs.filter(p => p.dp_txn_date != null || p.dp_description != null);

  // Step 1: remove pledge entries
  const nonPledgeDp = allDpPairs.filter(p => !isPledgeDescription(p.dp_description ?? ''));

  // Step 2: suppress eDIS noise pairs (no settlement code, cr+dr within 8 days)
  const edisNoCode = nonPledgeDp.filter(p =>
    isEdisDpLine(p.dp_description ?? '') && extractSettlementCode(p.dp_description ?? '') === null
  );
  const suppressed = new Set<string>();
  const crByQty = new Map<string, ReconTxnPair[]>();
  for (const p of edisNoCode) {
    const cr = Number(p.dp_credit ?? 0);
    if (cr <= 0) continue;
    const key = `${cr}`;
    if (!crByQty.has(key)) crByQty.set(key, []);
    crByQty.get(key)!.push(p);
  }
  for (const p of edisNoCode) {
    const dr = Number(p.dp_debit ?? 0);
    if (dr <= 0) continue;
    const candidates = (crByQty.get(`${dr}`) ?? []).filter(c => !suppressed.has(c.id));
    const drDate = p.dp_txn_date ? new Date(p.dp_txn_date).getTime() : null;
    if (!drDate) continue;
    const match = candidates.find(c => {
      const crDate = c.dp_txn_date ? new Date(c.dp_txn_date).getTime() : null;
      return crDate !== null && Math.abs(drDate - crDate) <= 8 * 24 * 60 * 60 * 1000;
    });
    if (match) { suppressed.add(p.id); suppressed.add(match.id); }
  }
  // Suppress lone By eDIS credits with no settlement code
  for (const p of edisNoCode) {
    const cr = Number(p.dp_credit ?? 0);
    if (cr > 0 && !suppressed.has(p.id)) suppressed.add(p.id);
  }

  const dpPairs = nonPledgeDp.filter(p => !suppressed.has(p.id));

  const dp_rows: DpTrace[] = dpPairs.map(p => {
    const desc   = p.dp_description ?? '';
    const credit = Number(p.dp_credit ?? 0);
    const debit  = Number(p.dp_debit  ?? 0);
    return {
      pair_id: p.id,
      date: p.dp_txn_date,
      description: desc,
      credit,
      debit,
      signed_qty: credit - debit,
      sub_section: inferSubSection(desc),
      slb_classification: classifySlb(desc, credit, debit),
    };
  });
  const dp_net = dp_rows
    .filter(r => !intradayExcludedDpIds.has(r.pair_id))
    .reduce((s, r) => s + r.signed_qty, 0);

  // Group by inferred sub-section. Order is: main, pledge, slb, then
  // anything else alphabetically — matches the typical document order.
  const sectionOrder = (s: string) => s === 'main' ? 0 : s === 'pledge' ? 1 : s === 'slb' ? 2 : 3;
  const by_section_map = new Map<string, DpTrace[]>();
  for (const r of dp_rows) {
    const arr = by_section_map.get(r.sub_section) ?? [];
    arr.push(r);
    by_section_map.set(r.sub_section, arr);
  }
  const by_section = Array.from(by_section_map.entries())
    .sort((a, b) => {
      const ord = sectionOrder(a[0]) - sectionOrder(b[0]);
      return ord !== 0 ? ord : a[0].localeCompare(b[0]);
    })
    .map(([section, rows]) => ({
      section,
      rows,
      subtotal: rows.reduce((s, r) => s + r.signed_qty, 0),
    }));

  // ─── SLB FIFO pairing ───────────────────────────────────────────────
  // Mirror of txnMatchEngine.computeSlbOutstanding. Walk in date order;
  // each return leg consumes the earliest open first leg of matching
  // qty (or earliest open first leg if no exact match).
  const slbSorted = [...dp_rows]
    .filter(r => r.slb_classification !== null)
    .sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''));
  const openFirsts: Array<{ pair_id: string; date: string; qty: number }> = [];
  const matched: SlbPairing['matched'] = [];
  for (const r of slbSorted) {
    if (r.slb_classification === 'first') {
      openFirsts.push({ pair_id: r.pair_id, date: r.date ?? '', qty: r.debit });
    } else if (r.slb_classification === 'return') {
      const returnQty = r.credit;
      const exactIdx = openFirsts.findIndex(o => Math.abs(o.qty - returnQty) <= QTY_TOLERANCE);
      if (exactIdx >= 0) {
        const f = openFirsts[exactIdx];
        matched.push({
          first_pair_id: f.pair_id, first_date: f.date, first_qty: f.qty,
          return_pair_id: r.pair_id, return_date: r.date ?? '', return_qty: returnQty,
          exact_match: true,
        });
        openFirsts.splice(exactIdx, 1);
      } else if (openFirsts.length > 0) {
        const f = openFirsts[0];
        matched.push({
          first_pair_id: f.pair_id, first_date: f.date, first_qty: f.qty,
          return_pair_id: r.pair_id, return_date: r.date ?? '', return_qty: returnQty,
          exact_match: false,
        });
        f.qty -= returnQty;
        if (f.qty <= QTY_TOLERANCE) openFirsts.shift();
      }
    }
  }
  const slb: SlbPairing = {
    unmatched_firsts: openFirsts,
    matched,
    outstanding: openFirsts.reduce((s, o) => s + o.qty, 0),
  };

  // ─── Status reason ──────────────────────────────────────────────────
  // The status comes from the backend (row.status — authoritative). We
  // just describe WHY in english based on the trace.
  let status_reason: string;
  const diff = Math.abs(pf_net - dp_net);
  if (row.status === 'MATCH') {
    status_reason = `|${pf_net} − ${dp_net}| = ${diff.toFixed(3)} ≤ tolerance ${QTY_TOLERANCE} → MATCH`;
  } else if (row.status === 'SLB_OUTSTANDING') {
    status_reason = `dp_net (${dp_net}) + slb_outstanding (${slb.outstanding}) ≈ portfolio_net (${pf_net}) → SLB_OUTSTANDING`;
  } else if (row.status === 'QTY_MISMATCH') {
    status_reason = `|${pf_net} − ${dp_net}| = ${diff.toFixed(3)} > tolerance ${QTY_TOLERANCE} → QTY_MISMATCH`;
  } else if (row.status === 'ONLY_IN_PORTFOLIO') {
    status_reason = `ISIN has portfolio activity (${pf_rows.length} txns) but no DP rows → ONLY_IN_PORTFOLIO`;
  } else if (row.status === 'ONLY_IN_DP') {
    status_reason = `ISIN has DP activity (${dp_rows.length} txns) but no portfolio rows → ONLY_IN_DP`;
  } else {
    status_reason = `status ${row.status} (legacy or computed upstream)`;
  }

  return {
    isin: row.canonical_isin,
    display_name: row.display_name ?? row.canonical_isin,
    pf: { rows: pf_rows, net: pf_net },
    dp: { rows: dp_rows, by_section, net: dp_net },
    slb,
    status: row.status,
    status_reason,
  };
}
